import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link, useParams } from "react-router-dom";
import "./OrderDetails.css";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL;
const API = `${API_BASE_URL}/api/user` ;

function OrderDetails() {
  const { id } = useParams();
  const token = localStorage.getItem("token");

  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!token) {
      setError("Please login to view order details.");
      setLoading(false);
      return;
    }

    loadOrder();
  }, [id, token]);

  const loadOrder = async () => {
    try {
      setLoading(true);
      setError("");

      const res = await axios.get(`${API}/orders/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      setOrder(res.data);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to load order details.");
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (date) => {
    if (!date) return "-";
    return new Date(date).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const orderStatus = useMemo(() => {
    return order?.status || order?.orderStatus || "Placed";
  }, [order]);

  const paymentStatus = useMemo(() => {
    return order?.paymentStatus || "Pending";
  }, [order]);

  const orderNumber = useMemo(() => {
    return order?.orderNumber || order?._id?.slice(-8)?.toUpperCase() || "-";
  }, [order]);

  if (loading) {
    return (
      <div className="od-page">
        <div className="od-card">
          <div className="od-loading">
            <div className="od-loading-spinner"></div>
            <h3>Loading order details...</h3>
            <p>Please wait while we fetch your order summary.</p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="od-page">
        <div className="od-card">
          <div className="od-empty">
            <div className="od-empty-icon">
              <i className="fas fa-circle-exclamation"></i>
            </div>
            <h3>Unable to Load Order</h3>
            <p>{error || "Order not found."}</p>
            <Link to="/orders" className="theme-btn">
              Back to Orders
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="od-page">
      <div className="od-card">
        <div className="od-topbar">
          <Link to="/orders" className="od-back-link">
            <i className="fas fa-arrow-left"></i> Back to Orders
          </Link>
        </div>

        <div className="od-hero">
          <div className="od-hero-left">
            <span className="od-eyebrow">Order Details</span>
            <h1>Order #{orderNumber}</h1>
            <p>Placed on {formatDate(order.createdAt)}</p>
          </div>

          <div className="od-hero-right">
            <span className={`od-status ${orderStatus.toLowerCase()}`}>
              {orderStatus}
            </span>
            <span className="od-payment-pill">{paymentStatus}</span>
          </div>
        </div>

        <div className="od-stats-grid">
          <div className="od-stat-card">
            <span>Payment Method</span>
            <strong>{order.paymentMethod || "Online"}</strong>
          </div>

          <div className="od-stat-card">
            <span>Payment ID</span>
            <strong>{order.razorpayPaymentId || "-"}</strong>
          </div>

          <div className="od-stat-card">
            <span>Total Items</span>
            <strong>{order.items?.length || 0}</strong>
          </div>

          <div className="od-stat-card">
            <span>Total Amount</span>
            <strong>₹{order.totalAmount || 0}</strong>
          </div>
        </div>

        <div className="od-layout">
          <div className="od-main">
            <section className="od-section">
              <div className="od-section-head">
                <h3>Items Ordered</h3>
              </div>

              <div className="od-items-list">
                {order.items?.length ? (
                  order.items.map((item, index) => (
                    <div
                      className="od-item-row"
                      key={item.productId || `${order._id}-${index}`}
                    >
                      <img src={item.image} alt={item.name} />

                      <div className="od-item-info">
                        <h4>{item.name}</h4>
                        <p>Quantity: {item.quantity}</p>
                        <p>Price per item: ₹{item.price}</p>
                      </div>

                      <div className="od-item-total">
                        ₹{(item.price || 0) * (item.quantity || 1)}
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="od-muted">No items found for this order.</p>
                )}
              </div>
            </section>

            {order.notes && (
              <section className="od-section">
                <div className="od-section-head">
                  <h3>Notes</h3>
                </div>
                <div className="od-note-box">
                  <p>{order.notes}</p>
                </div>
              </section>
            )}
          </div>

          <aside className="od-side">
            <section className="od-section od-side-card">
              <div className="od-section-head">
                <h3>Shipping Address</h3>
              </div>

              <div className="od-address-card">
                <h4>{order.shippingAddress?.fullName || "-"}</h4>
                <p>
                  {order.shippingAddress?.address || "-"}
                  {order.shippingAddress?.locality
                    ? `, ${order.shippingAddress.locality}`
                    : ""}
                </p>
                <p>
                  {order.shippingAddress?.city || "-"},{" "}
                  {order.shippingAddress?.state || "-"} -{" "}
                  {order.shippingAddress?.pincode || "-"}
                </p>
                <p>Phone: {order.shippingAddress?.phone || "-"}</p>
                <p>Landmark: {order.shippingAddress?.landmark || "-"}</p>
              </div>
            </section>

            <section className="od-section od-side-card">
              <div className="od-section-head">
                <h3>Price Breakdown</h3>
              </div>

              <div className="od-price-card">
                <div className="od-price-row">
                  <span>Subtotal</span>
                  <strong>₹{order.subtotal || 0}</strong>
                </div>

                <div className="od-price-row">
                  <span>Shipping Fee</span>
                  <strong>₹{order.shippingFee || 0}</strong>
                </div>

                <div className="od-price-row">
                  <span>Discount</span>
                  <strong>₹{order.discount || 0}</strong>
                </div>

                <div className="od-price-total">
                  <span>Total</span>
                  <strong>₹{order.totalAmount || 0}</strong>
                </div>
              </div>
            </section>
          </aside>
        </div>
      </div>
    </div>
  );
}

export default OrderDetails;